import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  REQUEST_STATUSES,
  REQUEST_TRIAGE,
  SERVICES,
  type RequestDto,
  type RequestStatus,
  type RequestTriage,
} from '@shared';
import { apiGet, apiPatch, ApiError } from '../lib/api';
import { StatusChip } from './StatusChip';

interface Lead {
  id: string;
  name: string;
  role: string;
  initials: string;
}

interface TriageDrawerProps {
  request: RequestDto;
  onClose: () => void;
  onPatched: (next: RequestDto) => void;
}

const TRIAGE_LABEL: Record<RequestTriage, string> = {
  unread: 'Unread',
  reviewing: 'Reviewing',
  proposed: 'Proposed',
  rejected: 'Rejected',
};

const STATUS_LABEL: Record<RequestStatus, string> = {
  submitted: 'Submitted',
  reviewing: 'Reviewing',
  engagement: 'Engagement',
  delivered: 'Delivered',
  closed: 'Closed',
};

const URGENCY_LABEL = { standard: 'Standard', expedited: 'Expedited', exploratory: 'Exploratory' };

// What the user has changed but not yet saved.
type Draft = {
  triage: RequestTriage;
  status: RequestStatus;
  assigned_lead_staff_id: string | null;
  value_pence: number | null;
};

function draftFrom(r: RequestDto): Draft {
  return {
    triage: r.triage,
    status: r.status,
    assigned_lead_staff_id: r.assigned_lead_staff_id ?? null,
    value_pence: r.value_pence ?? null,
  };
}

// Build a minimal patch body containing only the fields that actually changed.
function diffDraft(orig: Draft, next: Draft): Partial<Draft> {
  const out: Partial<Draft> = {};
  if (orig.triage !== next.triage) out.triage = next.triage;
  if (orig.status !== next.status) out.status = next.status;
  if (orig.assigned_lead_staff_id !== next.assigned_lead_staff_id) {
    out.assigned_lead_staff_id = next.assigned_lead_staff_id;
  }
  if (orig.value_pence !== next.value_pence) out.value_pence = next.value_pence;
  return out;
}

export function TriageDrawer({ request, onClose, onPatched }: TriageDrawerProps) {
  const original = useMemo(() => draftFrom(request), [request]);
  const [draft, setDraft] = useState<Draft>(original);
  const [valueInput, setValueInput] = useState<string>(
    request.value_pence != null ? (request.value_pence / 100).toString() : '',
  );
  const [leads, setLeads] = useState<Lead[] | null>(null);
  const [leadsError, setLeadsError] = useState<'forbidden' | 'other' | null>(null);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const firstFocusable = useRef<HTMLButtonElement>(null);
  const autoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset state whenever the request prop changes (drawer reopens for a different row).
  useEffect(() => {
    setDraft(draftFrom(request));
    setValueInput(request.value_pence != null ? (request.value_pence / 100).toString() : '');
    setError(null);
  }, [request]);

  // Load the active-lead list once on mount. Surface 403 as a hint under the
  // dropdown so analyst / compliance / finance roles understand why the picker
  // is empty rather than thinking the page is broken.
  useEffect(() => {
    apiGet<{ leads: Lead[] }>('/api/users/leads')
      .then((r) => {
        setLeads(r.leads);
        setLeadsError(null);
      })
      .catch((err) => {
        setLeads([]);
        if (err instanceof ApiError && err.status === 403) {
          setLeadsError('forbidden');
        } else {
          setLeadsError('other');
        }
      });
  }, []);

  // Close on Escape; lock body scroll while open. Clear any pending auto-close
  // timer when the drawer unmounts so an early close can't fire onClose twice.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    firstFocusable.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      if (autoCloseTimer.current) {
        clearTimeout(autoCloseTimer.current);
        autoCloseTimer.current = null;
      }
    };
  }, [onClose, saving]);

  const svc = SERVICES.find((s) => s.id === request.service);
  const patch = diffDraft(original, draft);
  const dirty = Object.keys(patch).length > 0;

  async function handleSave() {
    if (!dirty || saving || justSaved) return;
    setSaving(true);
    setError(null);
    try {
      const r = await apiPatch<{ request: RequestDto }>(`/api/requests/${request.id}`, patch);
      onPatched(r.request);
      setJustSaved(true);
      // Give the user ~1.2s to register the confirmation, then close.
      autoCloseTimer.current = setTimeout(() => {
        autoCloseTimer.current = null;
        onClose();
      }, 1200);
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.serverMessage
        : err instanceof Error ? err.message
        : 'Save failed';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  function commitValueInput() {
    const trimmed = valueInput.trim();
    if (trimmed === '') {
      setDraft((d) => ({ ...d, value_pence: null }));
      return;
    }
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n < 0) {
      setError('Engagement value must be a non-negative number (in £).');
      return;
    }
    setError(null);
    setDraft((d) => ({ ...d, value_pence: Math.round(n * 100) }));
  }

  return (
    <>
      <div className="drawer-backdrop" onClick={() => !saving && onClose()} aria-hidden="true" />
      <aside className="triage-drawer" role="dialog" aria-modal="true" aria-label={`Triage ${request.id}`}>
        <header className="drawer-hd">
          <div>
            <div className="rid">{request.id}</div>
            <h2 className="drawer-title">{request.headline}</h2>
            <div className="drawer-meta">
              <span>{request.client_name ?? request.client_id}</span>
              <span className="dot">·</span>
              <span>{svc?.name ?? request.service}</span>
              <span className="dot">·</span>
              <span>{URGENCY_LABEL[request.urgency]}</span>
              <span className="dot">·</span>
              <StatusChip status={request.status} />
            </div>
          </div>
          <button
            ref={firstFocusable}
            type="button"
            className="drawer-close"
            onClick={onClose}
            disabled={saving}
            aria-label="Close drawer"
          >
            ×
          </button>
        </header>

        <div className="drawer-body">
          <section className="drawer-sec">
            <div className="drawer-sec-lbl">Brief</div>
            <p className="drawer-desc">{request.description}</p>
            {request.counterparty && (
              <div className="drawer-kv">
                <span>Counterparty</span>
                <strong>{request.counterparty}</strong>
              </div>
            )}
            <div className="drawer-kv">
              <span>Submitted</span>
              <strong>{request.created_at.slice(0, 10)}</strong>
            </div>
          </section>

          <section className="drawer-sec">
            <div className="drawer-sec-lbl">Triage</div>
            <div className="chip-row">
              {REQUEST_TRIAGE.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`chip-btn ${draft.triage === t ? 'on' : ''} t-${t}`}
                  onClick={() => setDraft((d) => ({ ...d, triage: t }))}
                  disabled={saving}
                >
                  {TRIAGE_LABEL[t]}
                </button>
              ))}
            </div>
          </section>

          <section className="drawer-sec">
            <div className="drawer-sec-lbl">Status</div>
            <div className="chip-row">
              {REQUEST_STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`chip-btn ${draft.status === s ? 'on' : ''} s-${s}`}
                  onClick={() => setDraft((d) => ({ ...d, status: s }))}
                  disabled={saving}
                >
                  {STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </section>

          <section className="drawer-sec">
            <label className="drawer-sec-lbl" htmlFor="lead-select">Assigned lead</label>
            <select
              id="lead-select"
              className="drawer-select"
              value={draft.assigned_lead_staff_id ?? ''}
              disabled={saving || leads === null || leadsError === 'forbidden'}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  assigned_lead_staff_id: e.target.value === '' ? null : e.target.value,
                }))
              }
            >
              <option value="">— Unassigned —</option>
              {(leads ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({l.role})
                </option>
              ))}
            </select>
            {leads === null && !leadsError && <div className="drawer-hint">Loading leads…</div>}
            {leadsError === 'forbidden' && (
              <div className="drawer-hint" style={{ color: 'var(--fg-mute)' }}>
                Your role doesn’t allow reassigning leads. Ask an admin, lead, or super-admin to do this.
              </div>
            )}
            {leadsError === 'other' && (
              <div className="drawer-hint" style={{ color: 'var(--red, #c0392b)' }}>
                Could not load the lead list. Refresh the page or try again.
              </div>
            )}
          </section>

          <section className="drawer-sec">
            <label className="drawer-sec-lbl" htmlFor="value-input">Engagement value (£)</label>
            <input
              id="value-input"
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              className="drawer-input"
              value={valueInput}
              disabled={saving}
              onChange={(e) => setValueInput(e.target.value)}
              onBlur={commitValueInput}
              placeholder="e.g. 25000"
            />
          </section>
        </div>

        <footer className="drawer-ft">
          {error && <div className="drawer-error">{error}</div>}
          {justSaved && (
            <div
              role="status"
              aria-live="polite"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 14px',
                marginBottom: 10,
                borderRadius: 'var(--r-sm)',
                background: 'color-mix(in oklab, var(--green, #1f8a4c) 12%, transparent)',
                color: 'var(--green, #1f8a4c)',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              <span aria-hidden="true">✓</span>
              <span>Changes saved.</span>
            </div>
          )}
          {!dirty && !justSaved && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <Link
                to={`/admin/requests/${request.id}`}
                className="btn ghost"
                style={{ flex: 1, textAlign: 'center' }}
              >
                Open thread →
              </Link>
              {draft.triage === 'proposed' && (
                <Link
                  to={`/admin/proposals/new?request_id=${request.id}`}
                  className="btn"
                  style={{ flex: 1, textAlign: 'center' }}
                >
                  Build proposal →
                </Link>
              )}
            </div>
          )}
          <div className="drawer-actions">
            <button type="button" className="btn ghost" onClick={onClose} disabled={saving}>
              {justSaved ? 'Close now' : 'Cancel'}
            </button>
            <button
              type="button"
              className="btn"
              onClick={handleSave}
              disabled={!dirty || saving || justSaved}
            >
              {saving ? 'Saving…' : justSaved ? 'Saved ✓' : dirty ? 'Save changes' : 'No changes'}
            </button>
          </div>
        </footer>
      </aside>
    </>
  );
}
